import { AxiosError } from 'axios'
import axios, { pluckData } from 'src/utilities/http-client'
import {
  AccreditationReadiness,
  AccreditationReadinessEntry,
  AccreditationReadinessEntryWire,
  AccreditationReadinessVirginia,
  AccreditationReadinessVirginiaWire,
  AccreditationReadinessWire,
  ClassroomV2RejectionError,
  ClassroomV2RejectionResponse,
  CourseV2Accreditation,
  CourseV2AccreditationWire,
  CourseV2BlockWire,
  CourseV2Certificate,
  CourseV2CertificateWire,
  CourseV2Content,
  CourseV2ContentWire,
  CourseV2NextStep,
  CourseV2NextStepWire,
  CourseV2Progress,
  CourseV2ProgressWire,
  CourseV2QuizAnswerResponse,
  CourseV2QuizProgress,
  CourseV2QuizProgressWire,
  CourseV2QuizQuestion,
  CourseV2QuizResult,
  CourseV2QuizResultWire,
  CourseV2Step,
  CourseV2StepProgress,
  CourseV2StepProgressWire,
  CourseV2StepWire,
  CourseV2SurveyProgress,
  CourseV2SurveyProgressWire,
  CourseV2SurveyState,
  CourseV2SurveyStateWire,
  CourseV2SurveySubmission,
  CourseV2SurveySubmissionResponse,
  CourseV2SurveySubmissionResponseWire,
  ShowNotesBlock,
  VirginiaProviderReadiness,
  VirginiaProviderReadinessWire,
} from 'src/v3/classroom-v2/types'

// Returns the structured rejection body for a 409 from one of the three
// classroom-v2 write endpoints, or null for any other error (including
// other status codes, which stay plain text — see 403/404).
export let getRejectionError = (
  error: unknown,
): ClassroomV2RejectionResponse | null => {
  if (!(error instanceof AxiosError)) return null
  if (error.response?.status !== 409) return null
  return (error.response?.data as ClassroomV2RejectionResponse) ?? null
}

// True for a 404, which several of these endpoints use to mean "no such record
// yet" rather than "something went wrong" — see fetchCourseQuizResult.
export let isNotFoundError = (error: unknown): boolean =>
  error instanceof AxiosError && error.response?.status === 404

// The plain-text body of a 400, or null if this wasn't one. The survey POST uses
// it to list Likert fields that fell outside 1–5; unlike the 409s, there is no
// structured code to switch on, so the message IS the payload here.
export let getBadRequestMessage = (error: unknown): string | null =>
  error instanceof AxiosError && error.response?.status === 400
    ? ((error.response?.data as string) ?? '')
    : null

// The quiz 409s that mean this client's picture of the attempt has fallen out of
// step with the server's, rather than that a write failed and can be retried:
//
//   QuizSessionAlreadyScored    — the attempt is closed; nothing more can be
//                                 recorded against it.
//   QuizQuestionAlreadyAnswered — the server already holds an answer for this
//                                 question, so re-posting can't help and the
//                                 answer isn't lost.
//   QuizQuestionNotInQuiz       — the client is asking about a question this
//                                 quiz doesn't contain, so its question set is
//                                 stale.
//
// None of the three is a retry candidate: retrying re-sends the same request
// that the server has already rejected on grounds that won't change. The only
// correct response is to re-read the attempt from the server and render whatever
// it actually says — see resyncQuizAttempt in the wizard slice.
export let isAttemptDesyncError = (
  rejection: ClassroomV2RejectionError,
): boolean =>
  rejection === 'QuizSessionAlreadyScored' ||
  rejection === 'QuizQuestionAlreadyAnswered' ||
  rejection === 'QuizQuestionNotInQuiz'

// The two survey 409s that are NOT failures. Both mean the submission was
// refused because the user is already past this step, so neither is something to
// show them as an error:
//
//   SurveyAlreadySubmitted — this attempt's survey is already on record. The
//                            work isn't lost; it was already done.
//   SurveyNotRequired      — the course carries no survey-requiring
//                            accreditation, so there was never anything to
//                            submit.
//
// Distinct from SurveyPrerequisitesNotMet, which is a real refusal with work
// still owed — that one carries unmetPrerequisites and sends the user back.
export let isSurveyAlreadySettled = (
  rejection: ClassroomV2RejectionError,
): boolean =>
  rejection === 'SurveyAlreadySubmitted' || rejection === 'SurveyNotRequired'

// The two certificate answers that end the poll: notEarned means the learner has
// outstanding work and nothing is coming until that changes, and issued means it
// has arrived. pending is the only one worth asking again about.
//
// Lives next to the other classifiers (isAttemptDesyncError, isSurveyAlreadySettled)
// so the stopping rule is written once and the middleware's loop and any surface
// wondering whether a spinner is still justified read the same definition.
export let isCertificateResolved = (
  certificate: CourseV2Certificate,
): boolean => certificate.kind !== 'pending'

// Called only in the branch after every wire case has been handled, where the
// remaining type is `never`. It exists so that adding a case to a *Wire union
// without handling it here fails to compile, instead of falling through to a
// null that some component then has to defend against. The runtime throw is
// unreachable by construction; it's there for a server that sends a case this
// build doesn't know about, which is a bug worth surfacing loudly rather than
// silently dropping a step or a progress record.
let unhandledWireCase = (wire: never): never => {
  // eslint-disable-next-line fp/no-throw
  throw new Error(`Unhandled wire case: ${JSON.stringify(wire)}`)
}

type CourseVersionResponse = {
  version: number
  publishedVersion?: number
}

export let fetchCourseVersion = (courseID: string) =>
  axios
    .get<CourseVersionResponse>(
      `/api/v4/classroom/course/${encodeURIComponent(courseID)}/version`,
    )
    .then(pluckData)

// The one materializer that still needs a cast, because it's generic over
// every accreditation body: the case key IS the `kind` tag, so the transform is
// a single rename that TypeScript can't verify per-case without an eight-arm
// switch that adds no information. The exhaustiveness guarantee is upstream
// instead — CourseV2AccreditationWire is *derived* from CourseV2Accreditation,
// so the two unions cannot drift apart in the first place.
let materializeAccreditation = (
  wire: CourseV2AccreditationWire,
): CourseV2Accreditation => {
  let [kind, fields] = Object.entries(wire)[0]
  return { ...fields, kind } as CourseV2Accreditation
}

let materializeBlock = (wire: CourseV2BlockWire): ShowNotesBlock => {
  if (wire.textBlock) return { ...wire.textBlock, kind: 'textBlock' }
  if (wire.imageBlock) return { ...wire.imageBlock, kind: 'imageBlock' }
  if (wire.pdfBlock) return { ...wire.pdfBlock, kind: 'pdfBlock' }
  return unhandledWireCase(wire)
}

let materializeStep = (wire: CourseV2StepWire): CourseV2Step => {
  if (wire.video) return wire.video
  if (wire.showNotes) {
    return {
      ...wire.showNotes,
      blocks: wire.showNotes.blocks.map(materializeBlock),
    }
  }
  return unhandledWireCase(wire)
}

let materializeCourseContent = (
  wire: CourseV2ContentWire,
): CourseV2Content => ({
  ...wire,
  steps: wire.steps.map(materializeStep),
  accreditation: wire.accreditation.map(materializeAccreditation),
})

export let fetchCourseV2Content = (courseID: string) =>
  axios
    .get<CourseV2ContentWire>(
      `/api/classroom/v2/course/${encodeURIComponent(courseID)}/content`,
    )
    .then(pluckData)
    .then(materializeCourseContent)

// Absent-key → null, in one place. The API omits every optional field rather
// than sending null (NullValueHandling.Ignore), so this reads with `??` on the
// wire side and hands back the `| null` domain shape the rest of the codebase
// uses. Nothing downstream should be testing `'areaNumberALS' in virginia`.
let materializeVirginiaProvider = (
  wire: VirginiaProviderReadinessWire,
): VirginiaProviderReadiness => ({
  level: wire.level,
  areaNumber: wire.areaNumber ?? null,
  category: wire.category,
})

let materializeVirginiaReadiness = (
  wire: AccreditationReadinessVirginiaWire,
): AccreditationReadinessVirginia => ({
  topic: wire.topic,
  subTopic: wire.subTopic,
  category: wire.category,
  areaNumberALS: wire.areaNumberALS ?? null,
  areaNumberBLS: wire.areaNumberBLS ?? null,
  provider: wire.provider ? materializeVirginiaProvider(wire.provider) : null,
})

let materializeReadinessEntry = (
  wire: AccreditationReadinessEntryWire,
): AccreditationReadinessEntry => ({
  body: wire.body,
  willAutoSubmit: wire.willAutoSubmit,
  missingFields: wire.missingFields,
  virginia: wire.virginia ? materializeVirginiaReadiness(wire.virginia) : null,
})

// Served as serialized JSON but with Content-Type: text/plain
// (ctx.WriteTextAsync — pre-existing across this API). No special handling is
// needed: axios's default transformResponse JSON.parses a string body regardless
// of content type, and http-client.ts overrides neither transformResponse nor
// responseType. Documented because the mismatch looks like a bug otherwise.
//
// A 403 with a plain-text body means the caller isn't enrolled. The card treats
// any failure as "nothing to show" rather than surfacing an error, which is the
// right call for a 403 in particular — a learner who can't see the course has no
// use for its CE readiness.
export let fetchAccreditationReadiness = (courseID: string) =>
  axios
    .get<AccreditationReadinessWire>(
      `/api/classroom/v2/course/${encodeURIComponent(courseID)}/accreditation-readiness`,
    )
    .then(pluckData)
    .then((wire): AccreditationReadiness => ({
      accreditations: wire.accreditations.map(materializeReadinessEntry),
    }))

export let fetchShowNotesPDFUrl = (courseID: string, blockID: string) =>
  axios
    .get<string>(
      `/api/classroom/v2/course/${encodeURIComponent(courseID)}/show-notes/blocks/${encodeURIComponent(blockID)}/pdf`,
    )
    .then(pluckData)

export let postShowNotesStepCompleted = (
  courseID: string,
  showNotesID: string,
) =>
  axios.post(
    `/api/classroom/v2/course/${encodeURIComponent(courseID)}/progress/show-notes`,
    { showNotesID },
  )

// A 204 means the server treated this as a no-op (video already completed
// since the restart boundary) — nothing new was recorded, so callers should
// not treat it the same as a 200's "this progress/completion was accepted."
export type VideoWriteResult = 'recorded' | 'noOp'

export let postVideoProgress = (
  courseID: string,
  videoID: string,
  progressSeconds: number,
): Promise<VideoWriteResult> =>
  axios
    .post(
      `/api/classroom/v2/course/${encodeURIComponent(courseID)}/progress/video`,
      { videoID, progress: progressSeconds },
    )
    .then((response) => (response.status === 204 ? 'noOp' : 'recorded'))

// On a 409, the response body is a structured ClassroomV2RejectionResponse
// (see getRejectionError above) — use-video-compliance.ts switches on its
// `error` field to pick the right interruption dialog.
export let postVideoStepCompleted = (
  courseID: string,
  videoID: string,
): Promise<VideoWriteResult> =>
  axios
    .post(
      `/api/classroom/v2/course/${encodeURIComponent(courseID)}/stream-event/video`,
      { videoID },
    )
    .then((response) => (response.status === 204 ? 'noOp' : 'recorded'))

export let fetchVideoTranscriptVttUrl = (courseID: string, videoID: string) =>
  axios
    .get<string>(
      `/api/classroom/v2/course/${encodeURIComponent(courseID)}/video/${encodeURIComponent(videoID)}/transcript/vtt`,
    )
    .then(pluckData)

// F# None serializes as an ABSENT key, not null — so `nextStep` missing and
// `nextStep: null` mean the same thing (no scored attempt to route from yet)
// and both must land on null here. The == null check covers both deliberately.
// Exported: the self-paced progress rollup (self-paced/utils/course-progress.ts)
// reuses this rather than re-implementing the same wire vocabulary.
export let materializeNextStep = (
  wire: CourseV2NextStepWire | null | undefined,
): CourseV2NextStep | null => {
  if (wire == null) return null
  if (wire === 'claimCertificate') return { kind: 'claimCertificate' }
  if (wire === 'submitSurvey') return { kind: 'submitSurvey' }
  // retakeQuiz is the only payload-carrying case, and a single-member union has
  // nothing to narrow away — so there's no `never` branch left for
  // unhandledWireCase to sit in. The annotation does that job instead: if the
  // wire union gains a case, this object stops being assignable and the
  // materializer fails to compile.
  let { retakeQuiz }: { retakeQuiz: [score: number, passingScore: number] } =
    wire
  let [score, passingScore] = retakeQuiz
  return { kind: 'retakeQuiz', score, passingScore }
}

let materializeQuizResult = (
  wire: CourseV2QuizResultWire,
): CourseV2QuizResult => ({
  result: wire.result,
  answers: wire.answers,
  nextStep: materializeNextStep(wire.nextStep),
  certificate: wire.certificate,
})

let quizBase = (courseID: string) =>
  `/api/classroom/v2/course/${encodeURIComponent(courseID)}/quiz`

// Opens the attempt. Reuse-not-create: a mid-attempt reload gets the SAME
// session back with its answers intact, and someone who already passed gets
// the session they passed rather than a fresh attempt. So this is safe (and
// required) on every entry into the quiz, and the client never has to track
// whether a session already exists.
//
// This is the only quiz endpoint that enforces the prior-steps gate, so it's
// the one that can 409 with PriorStepsNotCompleted.
export let postCourseQuizSession = (courseID: string) =>
  axios
    .post<{ sessionID: string }>(`${quizBase(courseID)}/session`)
    .then(pluckData)
    .then((data) => data.sessionID)

// The response is state-dependent and always called the same way: a passed
// attempt returns every question unshuffled, a failed one returns a fresh
// randomized draw, a resumed one freezes answered questions in place and
// shuffles only what's unreached.
//
// Array order IS the order — deliberately not re-sorted by `sequence`, because
// for a randomized quiz the array is the shuffle and sequence is the authored
// order, so sorting would undo the randomization the server just did.
export let fetchCourseQuizQuestions = (courseID: string) =>
  axios
    .get<CourseV2QuizQuestion[]>(`${quizBase(courseID)}/questions`)
    .then(pluckData)

// Locks a question into its position, so a later reload can't shuffle away the
// question the user is currently looking at. Called when a question is
// displayed; skipping it is what would allow that swap.
export let postCourseQuizQuestionDisplayed = (
  courseID: string,
  sessionID: string,
  questionID: string,
) =>
  axios.post(`${quizBase(courseID)}/session/question`, {
    sessionID,
    questionID,
  })

export let postCourseQuizAnswer = (
  courseID: string,
  sessionID: string,
  questionID: string,
  answerID: string,
) =>
  axios
    .post<CourseV2QuizAnswerResponse>(`${quizBase(courseID)}/session/answer`, {
      sessionID,
      questionID,
      answerID,
    })
    .then(pluckData)

// Scores the attempt. Idempotent — a double-tap or a retry after a dropped
// response returns the ORIGINAL score rather than writing a second result row,
// so this is safe to retry.
export let postCourseQuizResult = (courseID: string, sessionID: string) =>
  axios
    .post<CourseV2QuizResultWire>(`${quizBase(courseID)}/session/result`, {
      sessionID,
    })
    .then(pluckData)
    .then(materializeQuizResult)

// The reload path for an already-scored attempt: same body as the result POST,
// including the same nextStep from the same backend function, so someone who
// force-quit between passing and submitting the survey comes back to exactly
// where they left off. 404s when there's no scored attempt since the last
// restart — which is a normal state, not an error.
export let fetchCourseQuizResult = (courseID: string) =>
  axios
    .get<CourseV2QuizResultWire>(`${quizBase(courseID)}/result`)
    .then(pluckData)
    .then(materializeQuizResult)

let materializeStepProgress = (
  wire: CourseV2StepProgressWire,
): CourseV2StepProgress => {
  if (wire.videoStep) {
    let [videoID, stepIndex, completed, progressSeconds] = wire.videoStep
    return {
      kind: 'videoStep',
      videoID,
      stepIndex,
      completed,
      progressSeconds,
    }
  }
  if (wire.showNotesStep) {
    let [showNotesID, stepIndex, completed] = wire.showNotesStep
    return { kind: 'showNotesStep', showNotesID, stepIndex, completed }
  }
  return unhandledWireCase(wire)
}

// Exported: the self-paced progress rollup (self-paced/utils/course-progress.ts)
// reuses this rather than re-implementing the same wire vocabulary.
export let materializeQuizProgress = (
  wire: CourseV2QuizProgressWire,
): CourseV2QuizProgress => {
  if (wire === 'notAttempted') return { kind: 'notAttempted' }
  if (wire === 'passed') return { kind: 'passed' }
  if (wire.inProgress !== undefined) {
    return { kind: 'inProgress', sessionID: wire.inProgress }
  }
  if (wire.failed !== undefined) {
    let [score, passingScore] = wire.failed
    return { kind: 'failed', score, passingScore }
  }
  return unhandledWireCase(wire)
}

// No longer the tag rename this used to be: `locked` and `submitted` now carry
// payloads, so the bare-string half and the single-key-object half need
// different treatment. Same shape as materializeQuizProgress — the string check
// is the first narrowing, and WireCases makes the rest exhaustive.
// Exported: the self-paced progress rollup (self-paced/utils/course-progress.ts)
// reuses this rather than re-implementing the same wire vocabulary.
export let materializeSurveyProgress = (
  wire: CourseV2SurveyProgressWire,
): CourseV2SurveyProgress => {
  if (wire === 'notRequired') return { kind: 'notRequired' }
  if (wire === 'notSubmitted') return { kind: 'notSubmitted' }
  if (wire.locked !== undefined) {
    return { kind: 'locked', unmetPrerequisites: wire.locked }
  }
  if (wire.submitted !== undefined) {
    return { kind: 'submitted', surveyID: wire.submitted }
  }
  return unhandledWireCase(wire)
}

// Order matters here, unusually for one of these. notEarned and issued are
// narrowed FIRST so that pending is what's left over — which is what lets a
// payload-less pending case (either the bare string or a single-key object whose
// value didn't survive serialization) resolve to a pending with no timestamp
// instead of falling through to unhandledWireCase and throwing on the one state
// the polling flow exists to handle. See CourseV2CertificateWire.
//
// The `?? null` is therefore load-bearing rather than defensive: it's the only
// thing standing between an omitted timestamp and a crash on the pending screen.
let materializeCertificate = (
  wire: CourseV2CertificateWire,
): CourseV2Certificate => {
  if (wire === 'pending') {
    return { kind: 'pending', requirementsCompletedTime: null }
  }
  if (wire.notEarned !== undefined) {
    return { kind: 'notEarned', unmetRequirements: wire.notEarned }
  }
  if (wire.issued !== undefined) {
    let {
      certificateID,
      downloadURL,
      issuedTime,
      educationalEventDate,
      requirementsCompletionDate,
    } = wire.issued
    return {
      kind: 'issued',
      certificate: {
        certificateID,
        downloadURL,
        // Absent optional fields are omitted from the JSON entirely rather than
        // sent as null, so these are ?? null rather than passed through.
        issuedTime: issuedTime ?? null,
        educationalEventDate: educationalEventDate ?? null,
        requirementsCompletionDate: requirementsCompletionDate ?? null,
      },
    }
  }
  return { kind: 'pending', requirementsCompletedTime: wire.pending ?? null }
}

// The poll endpoint, and the SOLE authority on whether a certificate exists or is
// on its way. Always 200 — it never 404s for "no certificate yet", because a 404
// would collapse "you haven't earned this" and "it's being generated right now"
// into one indistinguishable answer.
//
// Deliberately used for polling in preference to /progress, which recomputes the
// full course projection across nine readers per call.
//
// Every response mints a fresh presigned downloadURL, so re-fetching this is also
// the supported way to recover from an expired link.
export let fetchCourseCertificate = (courseID: string) =>
  axios
    .get<CourseV2CertificateWire>(
      `/api/classroom/v2/course/${encodeURIComponent(courseID)}/certificate`,
    )
    .then(pluckData)
    .then(materializeCertificate)

let materializeCourseProgress = (
  wire: CourseV2ProgressWire,
): CourseV2Progress => ({
  allStepsCompleted: wire.allStepsCompleted,
  steps: wire.steps.map(materializeStepProgress),
  quiz: materializeQuizProgress(wire.quiz),
  survey: materializeSurveyProgress(wire.survey),
  // State only — this arm can carry a presigned downloadURL, and it goes stale
  // sitting in the store. Nothing may render a link from the progress record;
  // fetchCourseCertificate is where a usable URL comes from.
  certificate: materializeCertificate(wire.certificate),
  // Absent until there's a scored attempt to route from. Same value the quiz
  // result endpoint returns, from the same backend function.
  nextStep: materializeNextStep(wire.nextStep),
})

export let fetchCourseV2Progress = (courseID: string) =>
  axios
    .get<CourseV2ProgressWire>(
      `/api/classroom/v2/course/${encodeURIComponent(courseID)}/progress`,
    )
    .then(pluckData)
    .then(materializeCourseProgress)

// Sets a new restart boundary: everything timestamped at or before restartedTime
// stops counting for this course, so the next /progress read comes back as a
// fresh attempt.
//
// No preconditions and NO 409, ever — it doesn't require the course to be
// complete and calling it repeatedly just appends another boundary. The only
// failure worth distinguishing is a 403, which the contract returns for "not
// yours" AND for a course that doesn't exist (the access check can't tell them
// apart) with a plain-text body. So there is nothing to parse and nothing to
// classify: any rejection means the restart didn't happen, which is the single
// thing a caller needs to know.
//
// restartedTime is returned and deliberately NOT retained anywhere. Its purpose
// per the contract is to let a client drop cached progress immediately rather
// than waiting on a follow-up GET — and this client does that by wiping the
// course's visit-scoped state in the fulfilled reducer, which needs the fact of
// the restart, not its timestamp. Keeping it would be a second, unread copy of
// a boundary the server already owns.
export type CourseRestartResponse = { restartedTime: string }

export let postCourseRestart = (courseID: string) =>
  axios
    .post<CourseRestartResponse>(
      `/api/classroom/v2/course/${encodeURIComponent(courseID)}/restart`,
    )
    .then(pluckData)

let surveyBase = (courseID: string) =>
  `/api/classroom/v2/course/${encodeURIComponent(courseID)}/survey`

// content/submittedTime are lifted onto the submitted arm rather than kept
// alongside the status, because the contract only ever populates them for a
// submitted survey — putting them where they're guaranteed means no consumer has
// to null-check them in a state where they could never have been sent.
let materializeSurveyState = (
  wire: CourseV2SurveyStateWire,
): CourseV2SurveyState => {
  let status = materializeSurveyProgress(wire.status)
  return status.kind === 'submitted'
    ? {
        kind: 'submitted',
        surveyID: status.surveyID,
        content: wire.content,
        submittedTime: wire.submittedTime,
      }
    : status
}

// The survey step's state without pulling the whole course projection. `status`
// is the same DU /progress reports, so this and the progress record can't
// disagree about whether a survey is open.
export let fetchCourseSurvey = (courseID: string) =>
  axios
    .get<CourseV2SurveyStateWire>(surveyBase(courseID))
    .then(pluckData)
    .then(materializeSurveyState)

// Append-only: no update, no delete, one submission per attempt. A repeat POST
// 409s with SurveyAlreadySubmitted rather than overwriting, which is why the
// client never needs to check for an existing submission first.
//
// nextStep in the response is always claimCertificate — the survey is the last
// gate — so the phase can advance off this without a progress refetch. The
// middleware refetches anyway, for the same reason the quiz writes do: the
// server's record, not this response, is what selectWizardPhase reads.
export let postCourseSurvey = (
  courseID: string,
  submission: CourseV2SurveySubmission,
) =>
  axios
    .post<CourseV2SurveySubmissionResponseWire>(
      surveyBase(courseID),
      submission,
    )
    .then(pluckData)
    .then((wire): CourseV2SurveySubmissionResponse => ({
      surveyID: wire.surveyID,
      nextStep: materializeNextStep(wire.nextStep),
      certificate: wire.certificate,
    }))
